import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { getToken } from "../api/utils";
import InviteLanding from "./InviteLanding";
import InviteExperience from "./InviteExperience";
import { getInviteThemeById } from "../data/inviteThemes";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function InviteThemeRenderer() {
  const { token, eventId, eventGuestId } = useParams();
  const location = useLocation();
  const [themeId, setThemeId] = useState(null);
  const [revealId, setRevealId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const themeOverride = location.state?.themeOverride || null;
  const revealOverride = location.state?.revealOverride || null;

  const endpoint = useMemo(() => {
    if (token) return `${API_BASE}/events/invite/${token}`;
    if (eventId && eventGuestId) return `${API_BASE}/events/${eventId}/guests/${eventGuestId}/preview`;
    if (eventId) return `${API_BASE}/events/${eventId}/experience`;
    return null;
  }, [eventGuestId, eventId, token]);

  useEffect(() => {
    let isMounted = true;

    const loadTheme = async () => {
      if (!endpoint) {
        if (isMounted) {
          setError("No invitation route was provided.");
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError("");
      try {
        const response = await fetch(endpoint, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${getToken() || ""}`,
          },
        });

        if (!response.ok) {
          let message = `Invite fetch failed (${response.status})`;
          try {
            const errorPayload = await response.json();
            message = errorPayload.detail || errorPayload.message || message;
          } catch (_error) {
            // ignore
          }
          throw new Error(message);
        }

        const payload = await response.json();
        const resolvedThemeId = themeOverride || payload?.event?.theme_id || "classic-professional";
        const resolvedRevealId = revealOverride || payload?.event?.reveal_id || "curtain-open";
        if (isMounted) {
          setThemeId(resolvedThemeId);
          setRevealId(resolvedRevealId);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || "Unable to load invitation theme.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadTheme();

    return () => {
      isMounted = false;
    };
  }, [endpoint, revealOverride, themeOverride]);

  if (loading) {
    return <p style={{ padding: "2rem", textAlign: "center" }}>Loading invitation...</p>;
  }

  if (error) {
    return <p style={{ padding: "2rem", textAlign: "center" }}>{error}</p>;
  }

  const theme = getInviteThemeById(themeId);

  if (theme?.componentKey === "royal-experience") {
    return <InviteExperience theme={theme} revealId={revealId} />;
  }

  return <InviteLanding themeId={themeId} theme={theme} revealId={revealId} />;
}
